import { useEffect, useMemo, useState } from 'react';
import { Divider, Loader, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@components/shared/PageHeader';
import { useAuthUser } from '@store/useAuthStore';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { useUserConfigurationsQuery } from '../../../hooks/queries/useUserConfigurationsQuery';
import { USER_ROUTES } from '../../../routes/userRoutes';
import { DeletePatientDialog } from '../components/DeletePatientDialog';
import { PatientsHubFilters } from '../components/PatientsHubFilters';
import { PatientsList } from '../components/PatientsList';
import {
  useDeletePatientMutation,
  usePatientsListQuery,
} from '../hooks/usePatientsQueries';
import type { PatientHubListRow } from '../types';
import {
  filterPatientsList,
  hasActivePatientsListFilters,
  type PatientListSort,
  type PatientMeasurementsFilter,
} from '../utils/filterPatientsList';
import { buildPatientHubListRows } from '../utils/patientHubList';

const DEFAULT_SORT: PatientListSort = 'code-asc';

export function PatientsHubPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const currentUser = useAuthUser();
  const patientsQuery = usePatientsListQuery();
  const sessionsQuery = useUserConfigurationsQuery(currentUser?.id?.toString());
  const deleteMutation = useDeletePatientMutation();
  const [patientToDelete, setPatientToDelete] =
    useState<PatientHubListRow | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [measurementsFilter, setMeasurementsFilter] =
    useState<PatientMeasurementsFilter>('all');
  const [sortBy, setSortBy] = useState<PatientListSort>(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isTablet = useMediaQuery('(max-width: 1024px)');

  const hubRows = useMemo(
    () =>
      buildPatientHubListRows(
        patientsQuery.data ?? [],
        (sessionsQuery.data ?? []) as DeviceConfigurationApi[]
      ),
    [patientsQuery.data, sessionsQuery.data]
  );

  const filterOptions = useMemo(
    () => ({
      search: searchValue,
      measurementsFilter,
      sortBy,
    }),
    [measurementsFilter, searchValue, sortBy]
  );

  const filteredPatients = useMemo(
    () => filterPatientsList(hubRows, filterOptions),
    [hubRows, filterOptions]
  );

  const tableData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredPatients.slice(startIndex, startIndex + pageSize);
  }, [filteredPatients, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchValue, measurementsFilter, sortBy]);

  const hasActiveFilters = hasActivePatientsListFilters(filterOptions);
  const isLoading = patientsQuery.isPending || sessionsQuery.isLoading;

  const handleResetFilters = () => {
    setSearchValue('');
    setMeasurementsFilter('all');
    setSortBy(DEFAULT_SORT);
  };

  const handleConfirmDelete = async () => {
    if (patientToDelete == null) return;

    await deleteMutation.mutateAsync(patientToDelete.id);
    setPatientToDelete(null);
  };

  return (
    <>
      <PageHeader
        title={t('left_bar_users', 'UTENTI')}
        subtitle={
          isMobile
            ? 'Rubrica utenti del tuo account'
            : 'Consulta e gestisci la rubrica utenti collegata al tuo account'
        }
      />
      <Divider mb='md' mt='md' />

      {isLoading ? (
        <Loader />
      ) : patientsQuery.isError ? (
        <Text c='red'>
          {patientsQuery.error instanceof Error
            ? patientsQuery.error.message
            : 'Impossibile caricare la lista utenti'}
        </Text>
      ) : (
        <Stack gap='lg'>
          {hasActiveFilters ? (
            <Text size='sm' c='dimmed'>
              Mostrando {filteredPatients.length} utent
              {filteredPatients.length !== 1 ? 'i' : 'e'} su {hubRows.length}{' '}
              totali
            </Text>
          ) : null}
          <PatientsHubFilters
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            measurementsFilter={measurementsFilter}
            onMeasurementsFilterChange={setMeasurementsFilter}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            filteredCount={filteredPatients.length}
            totalCount={hubRows.length}
            onReset={handleResetFilters}
            showReset={hasActiveFilters}
          />
          <PatientsList
            data={tableData}
            loading={isLoading}
            totalRecords={filteredPatients.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            hasActiveFilters={hasActiveFilters}
            deletingPatientId={
              deleteMutation.isPending ? patientToDelete?.id : null
            }
            isMobile={isMobile}
            isTablet={isTablet ?? false}
            onView={patient => navigate(USER_ROUTES.detail(patient.id))}
            onEdit={patient => navigate(USER_ROUTES.edit(patient.id))}
            onDelete={setPatientToDelete}
          />
        </Stack>
      )}

      <DeletePatientDialog
        patient={patientToDelete}
        opened={patientToDelete != null}
        loading={deleteMutation.isPending}
        onClose={() => setPatientToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
